import { useState } from 'react';
import { quizQuestions } from '../../../mocks/quiz-data';

interface QuizSectionProps {
  onComplete: (answers: Record<number, string>, userName: string) => void;
}

export default function QuizSection({ onComplete }: QuizSectionProps) {
  const [userName, setUserName] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const handleStart = () => {
    if (userName.trim()) {
      setHasStarted(true);
    }
  };

  const handleAnswer = (value: string) => {
    const newAnswers = { ...answers, [currentQuestion]: value };
    setAnswers(newAnswers);

    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      onComplete(newAnswers, userName);
      setTimeout(() => {
        const resultSection = document.getElementById('result-section');
        if (resultSection) {
          resultSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  if (!hasStarted) {
    return (
      <section id="quiz-section" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full mb-6">
              <i className="ri-questionnaire-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Bắt Đầu Trắc Nghiệm
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Trả lời 8 câu hỏi để khám phá phong cách BrandName phù hợp với bạn
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
            <div className="mb-8">
              <label htmlFor="userName" className="block text-lg font-semibold text-gray-700 mb-3">
                Họ và tên của bạn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="userName"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors"
                placeholder="Nhập họ và tên của bạn"
              />
            </div>

            <button
              onClick={handleStart}
              disabled={!userName.trim()}
              className="whitespace-nowrap w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-lg font-semibold rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Bắt Đầu Trắc Nghiệm
            </button>
          </div>
        </div>
      </section>
    );
  }

  const question = quizQuestions[currentQuestion];

  return (
    <section id="quiz-section" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-600">
              Câu hỏi {currentQuestion + 1}/{quizQuestions.length}
            </span>
            <span className="text-sm font-semibold text-teal-600">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 leading-relaxed">
            {question.question}
          </h3>

          <div className="space-y-4">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option.value)}
                className="whitespace-nowrap w-full text-left px-6 py-5 bg-gray-50 hover:bg-teal-50 border-2 border-gray-200 hover:border-teal-500 rounded-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white group-hover:bg-teal-100 border-2 border-gray-300 group-hover:border-teal-500 rounded-lg transition-all flex-shrink-0">
                    <span className="text-gray-600 group-hover:text-teal-600 font-bold">
                      {String.fromCharCode(65 + index)}
                    </span>
                  </div>
                  <span className="text-lg text-gray-700 group-hover:text-gray-900 font-medium">
                    {option.text}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {currentQuestion > 0 && (
            <button
              onClick={handlePrevious}
              className="whitespace-nowrap mt-8 px-6 py-3 text-gray-600 hover:text-gray-900 font-semibold transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Câu hỏi trước
            </button>
          )}
        </div>
      </div>
    </section>
  );
}